import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { ThemeProvider } from 'styled-components';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { theme } from '../theme/theme';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';

const container = document.createElement('div');

const DEFAULT_MESSAGE = 'You have unsaved changes on this page. Are you sure you want to leave?';

type ButtonVariant = React.ComponentProps<typeof Button>['variant'];

export interface ConfirmationDialogOptions {
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ButtonVariant;
  /** Side effect to run when the user confirms, before the promise resolves. */
  onConfirm?: () => void;
}

interface Props extends ConfirmationDialogOptions {
  resolve(value: boolean): void;
}

const ConfirmationDialog: React.FC<Props> = ({
  resolve,
  message = DEFAULT_MESSAGE,
  confirmLabel = 'Leave This Page',
  cancelLabel = 'Cancel',
  confirmVariant = 'error',
  onConfirm,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const unmount = () => {
    // Unmount the portal first before unmounting the entire component
    // to prevent the false "different copy of react" warning
    // https://github.com/facebook/react/issues/22343
    setIsOpen(false);

    setTimeout(() => {
      ReactDOM.unmountComponentAtNode(container);
    }, 0);
  };

  const onClose = () => {
    resolve(false);
    unmount();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Modal isOpen onRequestClose={onClose}>
        <ModalHeader variant="warning">Warning</ModalHeader>
        <ModalCloseButton onClick={onClose} />

        <ModalBody>{message}</ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose}>
            {cancelLabel}
          </Button>

          <Button
            variant={confirmVariant}
            onClick={() => {
              if (onConfirm) {
                onConfirm();
              }
              resolve(true);
              unmount();
            }}
            ml="12"
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </Modal>
    </ThemeProvider>
  );
};

/**
 * Imperatively shows an OPL confirmation dialog and resolves `true` when the
 * user confirms, `false` when they cancel/dismiss.
 *
 * Called with no arguments it preserves the original "navigate away" behaviour
 * (used as a `registerNavigateAwayCallback` handler), including unregistering
 * the callback on confirm.
 */
export const showOPLConfirmationDialog = (
  options?: ConfirmationDialogOptions,
): Promise<boolean> => {
  const resolvedOptions: ConfirmationDialogOptions = options ?? {
    onConfirm: () => ADV.util.Router.unregisterNavigateAwayCallback(),
  };

  return new Promise<boolean>(resolve => {
    ReactDOM.render(<ConfirmationDialog resolve={resolve} {...resolvedOptions} />, container);
  });
};
